import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useButtonAlertsPendingSync } from '../contexts/ButtonAlertsPendingSyncContext';
import { useDownloadManagerContext } from '../contexts/DownloadManagerContext';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import { Bell, User, ChevronDown, LogOut, Download, Video, ShieldCheck } from 'lucide-react';
import { isPendingButtonAlertSiteLinked } from '../utils/buttonAlertSiteLink';
import {
  ESCUDO_URBANO_ALERT_TITLE,
  formatRelativeTime,
} from '../utils/euEscudoUrbanoDisplay';
import {
  getAlarmPressCount,
  getEuAlarmTypeLabel,
  getMeta,
  getSiteAddressLine,
  getSiteDisplayName,
  type PendingButtonAlertRow,
} from '../utils/pendingButtonAlertRow';
import AlertAudioPlayButton from './AlertAudioPlayButton';
import './Header.css';
import {
  BUTTON_ALERT_COMMENT_MAX_LEN,
  BUTTON_ALERT_COMMENT_MIN_LEN
} from '../constants/buttonAlertManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const CAPTCHA_LEN = 5;
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCaptchaCode(): string {
  let s = '';
  for (let i = 0; i < CAPTCHA_LEN; i++) {
    s += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)];
  }
  return s;
}

function getInitials(user: { firstName?: string; lastName?: string; username?: string | null; email?: string } | null): string {
  if (!user) return 'U';
  const first = user.firstName || user.username || '';
  const last = user.lastName || '';
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (user.email) return user.email.slice(0, 2).toUpperCase();
  return 'U';
}

const Header: React.FC = () => {
  const { user, logout, token, isAuthenticated } = useAuth();
  const { pendingListGeneration, notifyPendingButtonAlertsChanged } = useButtonAlertsPendingSync();
  const { downloads, activeCount } = useDownloadManagerContext();
  const { subscribe } = useNotificationSocket();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingAlerts, setPendingAlerts] = useState<PendingButtonAlertRow[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [manageTarget, setManageTarget] = useState<PendingButtonAlertRow | null>(null);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [manageComment, setManageComment] = useState('');
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const manageModalRef = useRef<HTMLDivElement>(null);

  const loadPendingAlerts = useCallback(async (options?: { silent?: boolean }) => {
    if (!token || !isAuthenticated) {
      setPendingAlerts([]);
      return;
    }
    const silent = options?.silent === true;
    if (!silent) setPendingLoading(true);
    try {
      const res = await axios.get<{ success?: boolean; data?: PendingButtonAlertRow[] }>(
        `${API_URL}/button-alerts/pending`,
        { headers: { Authorization: `Bearer ${token}` }, params: { limit: 100 } }
      );
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setPendingAlerts(rows.filter(isPendingButtonAlertSiteLinked));
    } catch {
      setPendingAlerts([]);
    } finally {
      if (!silent) setPendingLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    void loadPendingAlerts();
  }, [loadPendingAlerts]);

  useEffect(() => {
    const unsub = subscribe<unknown>('notification', () => {
      void loadPendingAlerts({ silent: true });
    });
    return unsub;
  }, [subscribe, loadPendingAlerts]);

  useEffect(() => {
    if (showNotifications) void loadPendingAlerts();
  }, [showNotifications, loadPendingAlerts]);

  useEffect(() => {
    if (!token || !isAuthenticated) return;
    if (pendingListGeneration === 0) return;
    void loadPendingAlerts({ silent: true });
  }, [pendingListGeneration, token, isAuthenticated, loadPendingAlerts]);

  const openManageModal = (a: PendingButtonAlertRow) => {
    setCaptchaCode(randomCaptchaCode());
    setCaptchaInput('');
    setManageComment('');
    setManageTarget(a);
  };

  const closeManageModal = useCallback(() => {
    setManageTarget(null);
    setCaptchaInput('');
    setManageComment('');
    setCaptchaCode('');
  }, []);

  const closeNotificationsPanel = useCallback(() => {
    setShowNotifications(false);
    setManageTarget(null);
    setCaptchaInput('');
    setManageComment('');
    setCaptchaCode('');
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (manageModalRef.current?.contains(t)) return;
      if (dropdownRef.current && !dropdownRef.current.contains(t)) {
        setShowDropdown(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(t)) {
        setShowNotifications(false);
        closeManageModal();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeManageModal]);

  useEffect(() => {
    if (!manageTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeManageModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [manageTarget, closeManageModal]);

  const displayName = user?.firstName || user?.username || user?.email?.split('@')[0] || 'Usuario';
  const userRole = (user as { role?: string })?.role || 'Administrador';
  const pendingCount = pendingAlerts.length;

  const trimmedManageComment = manageComment.trim();
  const captchaOk = captchaInput.length === CAPTCHA_LEN && captchaInput === captchaCode;
  const commentOk =
    trimmedManageComment.length >= BUTTON_ALERT_COMMENT_MIN_LEN &&
    trimmedManageComment.length <= BUTTON_ALERT_COMMENT_MAX_LEN;
  const canConfirmManage = captchaOk && commentOk && !manageSubmitting;

  const confirmManage = useCallback(async () => {
    if (!manageTarget || !token) return;
    const commentText = manageComment.trim();
    if (
      captchaInput !== captchaCode ||
      commentText.length < BUTTON_ALERT_COMMENT_MIN_LEN ||
      commentText.length > BUTTON_ALERT_COMMENT_MAX_LEN
    ) {
      return;
    }
    setManageSubmitting(true);
    const id = manageTarget.id;
    try {
      await axios.patch(
        `${API_URL}/button-alerts/${id}/acknowledge`,
        { comment: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Alarma gestionada');
      setPendingAlerts((prev) => prev.filter((x) => x.id !== id));
      notifyPendingButtonAlertsChanged();
      closeManageModal();
    } catch {
      toast.error('No se pudo registrar la gestión');
    } finally {
      setManageSubmitting(false);
    }
  }, [
    manageTarget,
    token,
    captchaInput,
    captchaCode,
    manageComment,
    closeManageModal,
    notifyPendingButtonAlertsChanged,
  ]);

  const goToStreamsForAlert = (a: PendingButtonAlertRow) => {
    const siteName = getSiteDisplayName(a);
    navigate('/streams', {
      state: {
        siteId: a.siteId ?? undefined,
        siteName,
        fromMap: true,
        mapSessionId: Date.now(),
        buttonAlertId: a.id,
        latitude: typeof a.metadata?.latitud === 'number' ? a.metadata.latitud : undefined,
        longitude: typeof a.metadata?.longitud === 'number' ? a.metadata.longitud : undefined,
        deviceId: a.deviceId,
      },
    });
    closeNotificationsPanel();
  };

  const toggleNotifications = () => {
    if (showNotifications) {
      closeNotificationsPanel();
    } else {
      setShowNotifications(true);
    }
  };

  const manageCard =
    manageTarget != null
      ? {
          siteName: getSiteDisplayName(manageTarget),
          address: getSiteAddressLine(manageTarget),
          alarmLabel: getEuAlarmTypeLabel(manageTarget),
          when: formatRelativeTime(manageTarget.createdAt),
          pressCount: getAlarmPressCount(manageTarget),
        }
      : null;

  return (
    <header className="app-header" id="app-header">
      <div className="header-content">
        <div className="header-left" />
        <div className="header-right">
          {downloads.length > 0 && (
            <div className="downloads-badge-wrap">
              <span className="downloads-badge-label" title="Descargas en curso">
                <Download size={20} aria-hidden />
                {activeCount > 0 && (
                  <span className="notification-badge download-badge">{activeCount}</span>
                )}
              </span>
            </div>
          )}
          <div className="notifications" ref={notificationsRef}>
            <button
              className="notification-btn"
              type="button"
              onClick={toggleNotifications}
              title="Alertas de botón pendientes"
            >
              <Bell size={20} />
              {isAuthenticated && pendingCount > 0 && (
                <span className="notification-badge">{pendingCount > 99 ? '99+' : pendingCount}</span>
              )}
            </button>
            {showNotifications && isAuthenticated && (
              <div className="header-notifications-dropdown" role="menu">
                <div className="header-notifications-dropdown__head">
                  <span>Alertas no gestionadas</span>
                  {pendingLoading && <span className="header-notifications-dropdown__loading">…</span>}
                </div>
                <div className="header-notifications-dropdown__list">
                  {pendingAlerts.length === 0 && !pendingLoading ? (
                    <p className="header-notifications-dropdown__empty">No hay alertas pendientes</p>
                  ) : (
                    pendingAlerts.map((a) => {
                      const when = formatRelativeTime(a.createdAt);
                      const siteName = getSiteDisplayName(a);
                      const address = getSiteAddressLine(a);
                      const alarmLabel = getEuAlarmTypeLabel(a);
                      const pressCount = getAlarmPressCount(a);
                      const soundRaw = getMeta(a).sound;
                      const sound =
                        soundRaw != null && soundRaw !== '' ? Number(soundRaw) : undefined;
                      return (
                        <div key={a.id} className="header-notifications-dropdown__item">
                          <div className="header-notifications-dropdown__item-grid">
                            <AlertAudioPlayButton
                              buttonAlertId={a.id}
                              token={token}
                              sound={sound}
                              size={16}
                            />
                            <div className="header-notifications-dropdown__item-content">
                              <div className="header-notifications-dropdown__item-headline">
                                <h3 className="header-notifications-dropdown__item-title-eu">{ESCUDO_URBANO_ALERT_TITLE}</h3>
                                {when && (
                                  <time className="header-notifications-dropdown__item-when" dateTime={a.createdAt}>
                                    {when}
                                  </time>
                                )}
                              </div>
                              <div className="header-notifications-dropdown__item-block">
                                <span className="header-notifications-dropdown__prov-label">Ubicación</span>
                                <p className="header-notifications-dropdown__item-site-name">{siteName}</p>
                                <span className="header-notifications-dropdown__prov-label">Dirección</span>
                                <p className="header-notifications-dropdown__item-address">{address}</p>
                              </div>
                              <div className="header-notifications-dropdown__item-block">
                                <span className="header-notifications-dropdown__prov-label">Tipo de alarma</span>
                                <p className="header-notifications-dropdown__item-alarm-type">{alarmLabel}</p>
                              </div>
                              <div className="header-notifications-dropdown__item-block">
                                <span className="header-notifications-dropdown__prov-label">Pulsaciones del botón</span>
                                <p className="header-notifications-dropdown__item-alarm-type map-alert-press-count">
                                  {pressCount}×
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="header-notifications-dropdown__item-actions-row">
                            <button
                              type="button"
                              className="header-notifications-dropdown__action header-notifications-dropdown__action--primary"
                              disabled={manageSubmitting && manageTarget?.id === a.id}
                              onClick={() => openManageModal(a)}
                            >
                              Gestionar
                            </button>
                            <button
                              type="button"
                              className="header-notifications-dropdown__action"
                              onClick={() => goToStreamsForAlert(a)}
                              title="Ver cámaras del sitio"
                            >
                              <Video size={14} aria-hidden />
                              Ver cámaras
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            {showNotifications && !isAuthenticated && (
              <div className="header-notifications-dropdown" role="menu">
                <p className="header-notifications-dropdown__empty">Inicia sesión para ver alertas pendientes</p>
              </div>
            )}
          </div>

          {manageTarget &&
            createPortal(
              <div
                ref={manageModalRef}
                className="header-alerts-manage-modal__backdrop"
                role="presentation"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeManageModal();
                }}
              >
                <div
                  className="header-alerts-manage-modal"
                  role="dialog"
                  aria-labelledby="header-alerts-manage-title"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="header-alerts-manage-modal__header">
                    <ShieldCheck size={22} className="header-alerts-manage-modal__header-icon" aria-hidden />
                    <h2 id="header-alerts-manage-title" className="header-alerts-manage-modal__title">
                      Confirmar gestión
                    </h2>
                    <p className="header-alerts-manage-modal__subtitle">
                      Completa la verificación y un comentario para registrar que atendiste esta alerta.
                    </p>
                  </div>
                  <div className="header-alerts-manage-modal__context">
                    {manageCard && manageTarget && (
                      <>
                        <div className="header-alerts-manage-modal__context-headline">
                          <span className="header-alerts-manage-modal__context-kind">{ESCUDO_URBANO_ALERT_TITLE}</span>
                          {manageCard.when && (
                            <span className="header-alerts-manage-modal__context-when">{manageCard.when}</span>
                          )}
                        </div>
                        <span className="header-alerts-manage-modal__context-sublabel">Ubicación</span>
                        <span className="header-alerts-manage-modal__context-value">{manageCard.siteName}</span>
                        <span className="header-alerts-manage-modal__context-sublabel">Dirección</span>
                        <span className="header-alerts-manage-modal__context-value">{manageCard.address}</span>
                        <span className="header-alerts-manage-modal__context-sublabel">Tipo de alarma</span>
                        <span className="header-alerts-manage-modal__context-alarm">{manageCard.alarmLabel}</span>
                        <span className="header-alerts-manage-modal__context-sublabel">Pulsaciones del botón</span>
                        <span className="header-alerts-manage-modal__context-alarm map-alert-press-count">
                          {manageCard.pressCount}×
                        </span>
                        <span className="header-alerts-manage-modal__context-sublabel">Mensaje</span>
                        <span className="header-alerts-manage-modal__context-msg">{manageTarget.message}</span>
                      </>
                    )}
                  </div>
                  <div className="header-alerts-manage-modal__field">
                    <label className="header-alerts-manage-modal__label" htmlFor="header-alerts-captcha">
                      Código de verificación <span className="header-alerts-manage-modal__hint">(copia el código)</span>
                    </label>
                    <div className="header-alerts-manage-modal__captcha-row" aria-hidden>
                      {captchaCode.split('').map((ch, i) => (
                        <span key={`${captchaCode}-${ch}-${i}`} className="header-alerts-manage-modal__captcha-char">
                          {ch}
                        </span>
                      ))}
                    </div>
                    <input
                      id="header-alerts-captcha"
                      className="header-alerts-manage-modal__input"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={CAPTCHA_LEN}
                      placeholder={`Escribe los ${CAPTCHA_LEN} caracteres`}
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    />
                    {captchaInput.length > 0 && !captchaOk && (
                      <span className="header-alerts-manage-modal__error">El código no coincide.</span>
                    )}
                  </div>
                  <div className="header-alerts-manage-modal__field">
                    <label className="header-alerts-manage-modal__label" htmlFor="header-alerts-comment">
                      Comentario de gestión{' '}
                      <span className="header-alerts-manage-modal__hint">
                        (mín. {BUTTON_ALERT_COMMENT_MIN_LEN}, máx. {BUTTON_ALERT_COMMENT_MAX_LEN} caracteres)
                      </span>
                    </label>
                    <textarea
                      id="header-alerts-comment"
                      className="header-alerts-manage-modal__textarea"
                      rows={4}
                      maxLength={BUTTON_ALERT_COMMENT_MAX_LEN}
                      placeholder="Describe brevemente la acción tomada…"
                      value={manageComment}
                      onChange={(e) => setManageComment(e.target.value)}
                    />
                    <span className="header-alerts-manage-modal__counter">
                      {trimmedManageComment.length} / {BUTTON_ALERT_COMMENT_MAX_LEN} · mín.{' '}
                      {BUTTON_ALERT_COMMENT_MIN_LEN}
                    </span>
                  </div>
                  <div className="header-alerts-manage-modal__actions">
                    <button
                      type="button"
                      className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--ghost"
                      onClick={closeManageModal}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--primary"
                      disabled={!canConfirmManage}
                      onClick={() => void confirmManage()}
                    >
                      {manageSubmitting ? 'Guardando…' : 'Confirmar gestión'}
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

          <div className="user-info-container" ref={dropdownRef}>
            <button
              type="button"
              className="user-info"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="user-avatar user-avatar-initials">
                {getInitials(user)}
              </div>
              <div className="user-details">
                <div className="user-name">{displayName}</div>
                <span className="user-role-badge">{userRole}</span>
              </div>
              <ChevronDown size={14} className={`dropdown-arrow ${showDropdown ? 'open' : ''}`} />
            </button>

            {showDropdown && (
              <div className="user-dropdown">
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/profile');
                  }}
                >
                  <User size={16} />
                  Perfil
                </button>
                <button
                  className="dropdown-item dropdown-item-logout"
                  onClick={() => {
                    setShowDropdown(false);
                    logout();
                  }}
                >
                  <LogOut size={16} />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
